import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { validateCredential, setPassword as setPasswordApi } from "@/lib/api";
import { saveAuth } from "@/lib/auth";
import { Loader2, ShieldCheck, KeyRound, CheckCircle2, XCircle } from "lucide-react";

/**
 * Shared account-setup / password-reset screen.
 * `mode` = "setup" (invite acceptance) | "reset" (password reset).
 */
export default function AdminSetPassword({ mode = "setup" }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const isSetup = mode === "setup";

  useEffect(() => {
    let active = true;
    (async () => {
      if (!token) {
        setError("Missing or malformed link.");
        setChecking(false);
        return;
      }
      try {
        const res = await validateCredential(token);
        if (!active) return;
        setValid(true);
        setInfo(res);
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.detail || "This link is invalid or has expired.");
      } finally {
        if (active) setChecking(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const data = await setPasswordApi(token, password);
      saveAuth(data.token, data.user);
      toast.success(isSetup ? "Your account is ready" : "Password updated");
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not complete. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen rd-bg text-white flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 relative rd-noise">
        <div className="absolute inset-0 rd-glow pointer-events-none" />
        <div className="relative w-full max-w-md">
          <div className="flex justify-center mb-8">
            <Logo height={40} themeAware />
          </div>
          <div
            className="rounded-2xl border border-white/10 p-8"
            style={{ backgroundColor: "#0F1424" }}
            data-testid="admin-setpassword-card"
          >
            {checking ? (
              <div className="flex items-center justify-center py-10 text-white/60">
                <Loader2 className="animate-spin mr-2" size={20} /> Verifying your link&hellip;
              </div>
            ) : !valid ? (
              <div className="text-center py-6" data-testid="admin-setpassword-invalid">
                <XCircle className="mx-auto text-[#FF5C7A]" size={40} />
                <h1 className="text-xl font-bold mt-4">Link unavailable</h1>
                <p className="text-white/60 text-sm mt-2">{error}</p>
                <Button
                  onClick={() => navigate("/admin")}
                  className="mt-6 bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-xl h-11 px-6"
                >
                  Go to sign in
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="admin-setpassword-form">
                <div className="flex items-center gap-2 mb-2">
                  <span className="rounded-lg bg-[#1E6BFF]/15 text-[#66B2FF] p-2">
                    {isSetup ? <ShieldCheck size={18} /> : <KeyRound size={18} />}
                  </span>
                  <h1 className="text-xl font-bold">
                    {isSetup ? "Set up your account" : "Reset your password"}
                  </h1>
                </div>
                <p className="text-white/60 text-sm mb-6">
                  {isSetup
                    ? "Welcome to TeamBuilder AI by Rendeavored."
                    : "Choose a new password for your account."}
                  {info?.email ? (
                    <>
                      {" "}
                      <span className="text-white/80">{info.email}</span>
                    </>
                  ) : null}
                </p>
                <div className="space-y-4">
                  <div>
                    <Label className="text-white/80 mb-2 block">New password</Label>
                    <Input
                      data-testid="admin-new-password-input"
                      type="password"
                      value={password}
                      onChange={(e) => setPw(e.target.value)}
                      placeholder="At least 8 characters"
                      className="bg-[#0A0F1E] border-white/10 h-11 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white/80 mb-2 block">Confirm password</Label>
                    <Input
                      data-testid="admin-confirm-password-input"
                      type="password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="Re-enter your password"
                      className="bg-[#0A0F1E] border-white/10 h-11 text-white"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={busy}
                    data-testid="admin-setpassword-submit"
                    className="w-full bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-xl h-11"
                  >
                    {busy ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <>
                        <CheckCircle2 size={18} className="mr-2" />
                        {isSetup ? "Activate account" : "Update password"}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
          <p className="text-center text-white/40 text-xs mt-6">
            &copy; 2026 Rendeavored Group, LLC &bull; TeamBuilder AI
          </p>
        </div>
      </div>
    </div>
  );
}
